/** The review page's tabs, with the keys of REVIEW_TABS in the backend. */
export const REVIEW_TABS = [
  {
    key: "tagged",
    label: "Ads tagged",
    hint: "Tags nobody has reviewed yet. Review the ones you want; the rest stay as tagged.",
  },
  {
    key: "reviewed",
    label: "Ads reviewed",
    hint: "Tags someone approved or corrected, newest first",
  },
  {
    key: "new",
    label: "New",
    hint: "Unreviewed tags with a topic or pairing that is new to the word list",
  },
];

export const DEFAULT_TAB = REVIEW_TABS[0].key;

/** Each tab's filters, with the keys of REVIEW_FILTERS in the backend. */
export const REVIEW_FILTERS = {
  tagged: [
    { value: "needs_review", label: "Needs review", alert: true },
    { value: "low_confidence", label: "Low confidence" },
    { value: "watched_exit", label: "Watched exits" },
    { value: "name_override", label: "Ad-name rule" },
  ],
  reviewed: [
    { value: "approved", label: "Approved" },
    { value: "corrected", label: "Corrected" },
  ],
  new: [
    { value: "coined", label: "New topics" },
    { value: "new_pair", label: "New pairings" },
  ],
};

export const REVIEWED_BY_OPTIONS = [{ value: "me", label: "Me" }];

export const TAG_FIELDS = [
  { key: "pitched_category", label: "Category" },
  { key: "pitched_subcategory", label: "Sub-category" },
  { key: "pitched_topic", label: "Topic" },
  { key: "pain_point", label: "Pain point" },
  { key: "pitch_type", label: "Pitch type" },
];

/** Fields that exist only inside the tag's own category; an exit clears them. */
export const IN_CATEGORY_FIELDS = [
  "pitched_subcategory",
  "pitched_topic",
  "pain_point",
];

export const FLAG_LABEL = {
  coined: "Coined",
  new_pair: "New pairing",
  ruling_fix: "Rule fix",
  name_override: "Ad-name rule",
  watched_exit: "Watched exit",
};

/** Wording for ReviewReason in the backend's creative_analysis/constants.py. */
export const REVIEW_REASON_LABEL = {
  invalid_category: "The category is not a known category",
  retired_subcategory: "The sub-category has been retired",
  invalid_subcategory: "The sub-category is not in the category's list",
  empty_topic: "No topic was given",
  bad_coinage: "The coined topic breaks the naming rules",
  invalid_pitch_type: "The pitch type is not a known type",
  refused: "The model declined to tag",
  topic_rejected: "The owner rejected this topic",
  topic_retired: "The topic has been retired",
  transcript_too_short: "The transcript is too short to tag",
};

/** Wording for the correction reasons the backend knows today; others are humanized. */
export const REASON_CODE_LABEL = {
  resolution_vs_hook: "Tagged the hook, not what the ad resolves",
  should_exit: "Belongs to another category",
  should_not_exit: "Belongs to this category",
  topic_near_miss: "Close, but a better topic exists",
  protagonist: "Wrong protagonist",
  name_override_wrong: "The ad-name rule was wrong",
  audience_wrong: "Wrong audience",
  transcript_bad: "The transcript is wrong or garbled",
  wound_as_subcategory: "Used the person's problem as the sub-category",
  wrong_system: "Wrong astrological system",
  dasha_vs_chart: "Mixed up a dasha with a chart reading",
  pain_hook_level: "Pain point not taken from the hook",
  health_boundary: "Crossed the line into Health",
  earning_boundary: "Crossed the line into earning or business",
  makeover_vs_makeup: "Mixed up Makeover and Makeup",
  condition_vs_situation: "Mixed up a skin or hair condition with a situation",
  other: "Other (explain in feedback)",
};

export const editorDefaultState = {
  values: {},
  reasons: {},
  notes: {},
  feedback: "",
};

/** Audience fields the model answers, shown for review with a note each; keys as in the backend's AUDIENCE_LISTS. */
export const AUDIENCE_FIELDS = [
  { key: "tg_age_gender", label: "Age and gender" },
  { key: "persona_bucket", label: "Persona" },
  { key: "emotional_angle_primary", label: "Emotional angle (primary)" },
  { key: "emotional_angle_secondary", label: "Emotional angle (secondary)" },
];

/** Notes with blank entries dropped and text trimmed. */
export const handleCleanNotes = ({ notes }) =>
  Object.fromEntries(
    Object.entries(notes || {})
      .map(([key, text]) => [key, (text || "").trim()])
      .filter(([, text]) => text)
  );

export const FEEDBACK_MAX_CHARS = 2000;

export const TAXONOMY_OPTIONS_STALE_MS = 5 * 60 * 1000;

export const handleHumanize = ({ code }) =>
  code ? code.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase()) : "";

export const handleReasonLabel = ({ code }) =>
  REASON_CODE_LABEL[code] || handleHumanize({ code });

/** A category name reduced to a-z and 0-9, as the backend matches category names. */
export const handleCategoryToken = ({ label }) =>
  (label || "").toLowerCase().replace(/[^a-z0-9]/g, "");

/** The model's reasoning key for each tag field it explains. */
export const REASONING_KEY_FOR_FIELD = {
  pitched_category: "category",
  pitched_subcategory: "subcategory",
  pitch_type: "pitch_type",
};

export const handleSplitReasoning = ({ reasoning }) => {
  const entries = Object.entries(reasoning || {}).filter(
    ([, text]) => typeof text === "string" && text.trim()
  );
  const fieldKeys = Object.values(REASONING_KEY_FOR_FIELD);
  const byField = Object.fromEntries(
    Object.entries(REASONING_KEY_FOR_FIELD).map(([field, key]) => [
      field,
      (reasoning || {})[key] || "",
    ])
  );
  return {
    byField,
    others: entries.filter(([key]) => !fieldKeys.includes(key)),
  };
};

export const handleTagValues = ({ tag }) =>
  Object.fromEntries(TAG_FIELDS.map(({ key }) => [key, tag?.[key] || ""]));
